import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../environments/environment';
import { ConsentService } from './consent.service';
import { loadThirdPartyScript } from './third-party-loader';

/** Firma della coda comandi di gtag.js (`gtag('js'|'config'|'event', …)`). */
type GtagFn = (...args: unknown[]) => void;

/**
 * ID del tag Google (account Google Ads). È un token pubblico — viaggia comunque nel JS
 * client — ed è identico in ogni ambiente: a cambiare è solo SE il tag parte, gated da
 * `environment.features.googleTag` (solo produzione).
 */
const TAG_ID = 'AW-18376025339';

/**
 * Possiede il tag Google (gtag.js) end-to-end, come `MetaPixelService` fa con il Pixel.
 *
 * Google suggerisce di incollare lo snippet in `<head>` su ogni pagina; qui non si può,
 * per due motivi:
 *  - `index.html` è UNA sola per tutto il sito (SSR + SPA), quindi lo snippet finirebbe
 *    anche in dev e staging, sporcando conversioni e audience dell'account Ads con
 *    traffico interno;
 *  - `gtag/js` è ~90 KB di terze parti sul thread principale, e la home è la pagina che
 *    porta l'LCP.
 * Quindi lo snippet viene ricostruito qui e iniettato a runtime da `boot()`, solo in
 * produzione; la coda parte subito e il download aspetta la prima interazione o 5 s dal
 * caricamento (sugli atterraggi da annuncio il primo slot idle: third-party-loader.ts).
 * Fuori produzione `gtag` non esiste proprio, e ogni helper di questo servizio è un no-op.
 */
@Injectable({ providedIn: 'root' })
export class GoogleTagService {
  private readonly isBrowser: boolean;
  private booted = false;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private readonly consent: ConsentService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * True quando il tag può girare: nel browser, in un ambiente dove è acceso (produzione) e
   * dopo che il visitatore ha accettato Google nel banner dei cookie (decisione D12).
   */
  get enabled(): boolean {
    return this.isBrowser && environment.features.googleTag && this.consent.granted('google');
  }

  /**
   * Inietta il tag Google e invia il page_view della landing (lo fa `config` da solo).
   * No-op se il tag non è abilitato — e idempotente: va chiamato una volta sola dal
   * componente root, dopo l'idratazione.
   */
  boot(): void {
    if (!this.enabled || this.booted) return;
    this.booted = true;

    const w = window as unknown as { dataLayer?: unknown[]; gtag?: GtagFn };
    if (w.gtag) return;

    const dataLayer = (w.dataLayer ??= []);
    // Lo snippet ufficiale è `function gtag(){dataLayer.push(arguments)}` e la `function`
    // classica NON è un dettaglio di stile: gtag.js riconosce un comando solo se in coda
    // trova l'oggetto `arguments`. Un array (quello che produrrebbe una arrow con rest)
    // viene interpretato come messaggio dati e il comando viene ignorato.
    const gtag: GtagFn = function () {
      // eslint-disable-next-line prefer-rest-params -- gtag.js vuole l'oggetto arguments, vedi sopra
      dataLayer.push(arguments);
    };
    w.gtag = gtag;

    // Come per il Pixel: la coda `dataLayer` è installata qui sopra, subito, e solo il
    // download di gtag.js (~90 KB) aspetta (third-party-loader.ts). Rimandare anche la
    // coda lasciava una finestra in cui `gtag` non esisteva e ogni evento spariva.
    loadThirdPartyScript(() => {
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${TAG_ID}`;
      document.head.appendChild(s);
    });

    gtag('js', new Date());
    // `config` fa da solo il page_view di atterraggio e, se l'URL arriva da un annuncio,
    // legge il `gclid` e scrive il cookie `_gcl_aw` da cui dipende l'attribuzione delle
    // conversioni. Sugli atterraggi da annuncio gtag.js parte al primo slot idle, così il
    // parametro è ancora nell'indirizzo quando arriva.
    gtag('config', TAG_ID);
  }

  /**
   * Page view di una navigazione SPA. Su una single page app `config` scatta una volta
   * sola, quindi i cambi di rotta vanno mandati a mano (la prima è già coperta da
   * `boot()`). No-op finché il tag non è stato caricato.
   */
  trackPageView(): void {
    this.event('page_view', {
      page_location: location.href,
      page_title: document.title,
    });
  }

  /**
   * Invia un evento gtag. No-op se il tag non è attivo, così i chiamanti non devono
   * gatearlo a loro volta.
   */
  event(name: string, params?: Record<string, unknown>): void {
    const gtag = this.gtag();
    if (!gtag) return;
    if (params) {
      gtag('event', name, params);
    } else {
      gtag('event', name);
    }
  }

  private gtag(): GtagFn | undefined {
    if (!this.enabled) return undefined;
    return (window as unknown as { gtag?: GtagFn }).gtag;
  }
}
